<script lang="ts">
	import {
		Modal,
		IconButton,
		Stack,
		Container,
		ModalContentLayout,
		NavigationItem,
		Breadcrumbs,
		Alert,
		Loader,
		LabelV2,
		Button,
		toast,
	} from '@Shelly/shelly-design-system/index';
	import SidePanelSettingsModalStickyBottom from './SidePanelSettingsModalStickyBottom.svelte';
	import { writable } from 'svelte/store';
	import type { NavigationMenuItemExtendedPropsTypes } from '../sidepanel-settings-modal';
	import { devices_config } from '@devices/index';
	import {
		get_mode,
		real_id,
		is_gen_2,
		isWallDisplay,
		get_local_ip,
		is_GBLE,
	} from 'src/helpers/interface_helpers';
	import { user_data } from 'src/stores/account/user';
	import { rebooting_devices } from 'src/stores/device-runtime/process';
	import { getDeviceSettings, getDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { getDeviceSettingsReadiness } from 'src/stores/device-runtime/readiness';
	import { deviceRuntimeResource } from 'src/stores/device-runtime/resource-state';
	import { devices } from 'src/stores/lists/devices';
	import { rooms } from 'src/stores/lists/rooms';
	import { app, os } from 'src/stores/session/native-bridge';
	import type { Device } from 'src/types/Device';
	import { sidePanelState } from 'src/stores/ui/sidepanel-public';
	import {
		getCriticalNotifications,
		getNotificationsForDevice,
	} from 'src/helpers/notifications/device_notifications_helper';
	import { onMount, tick, setContext } from 'svelte';
	import { replace } from 'svelte-spa-router';
	import { mobile } from 'src/services/mobile-rework';
	import {
		deviceSettingFormatter,
		handleCollapseTitleLabel,
	} from 'src/helpers/device_settings_helpers';
	import DeviceSettingsItem from 'src/components/DevicesSettings/DeviceSettingsItem.svelte';
	import { format } from 'svelte-i18n';
	import ThermostatsG2 from 'src/components/DevicesSettings/Settings/SettingsG2/ThermostatsWallDisplay/ThermostatsG2.svelte';
	import MediaPlayerComponent from 'src/components/MediaPlayerComponent.svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	import { getDeviceState } from 'src/stores/device-runtime/states';
	import { isTabDisabled } from '../sidepanel-tabs-helpers';
	import { routeToDeviceSettings } from 'src/stores/ui/route-to-device-settings';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { hasExternalSensors } from 'src/helpers/ext_sensor_helpers';
	import { isSupportedVersion } from 'src/helpers/inclusion_helpers';
	import BluetoothSettings from 'src/components/DevicesSettings/SettingsGBLE/BluetoothSettings.svelte';
	import { controlBleDevice } from 'src/stores/features/ble/control-device';
	import { control_bluetooth_device_ios } from 'src/services/native_container_requests_ios';
	import { control_bluetooth_device_android } from 'src/services/native_container_requests_android';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { localReq, postReq } from 'src/services/http';
	import DeleteDeviceModal from 'src/components/DevicesSettings/Settings/CommonSettings/DeviceInfo/DeleteDeviceModal.svelte';
	import ModalPrompt from 'src/components/ModalPrompt.svelte';
	import FactoryResetModal from 'src/components/FactoryResetModal.svelte';
	import BatchTransferModal from 'src/components/BatchCommandModals/BatchTransferModal.svelte';
	import { update } from 'src/stores/ui/edit_lists';
	import { deleteDevice as deleteDeviceHelper } from 'src/helpers/add_delete_helpers';
	import { resetDrawerClose } from 'src/helpers/drawer_helpers';
	import type { DeviceUsage } from 'src/components/DevicesSettings/Settings/CommonSettings/DeviceInfo/device_usage_helper';
	import {
		getGroupsWithDevice,
		getAlarmsWithDevice,
		getScenesWithDevice,
		getDashboardsWithDevice,
		getThermostatsWithDevice,
	} from 'src/components/DevicesSettings/Settings/CommonSettings/DeviceInfo/device_usage_helper';
	import { deleteWallDisplayExtention } from 'src/helpers/wall_display_extract/extracting_helpers';
	import { isXT1Module } from 'src/helpers/xt1_helpers/template_helpers';
	import {
		fetchXT1DeviceSettings,
		getXT1SettingsNavigationItems,
		type XT1DeviceSettings,
	} from 'src/helpers/xt1_helpers/template_settings_helpers';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import SharedHomeOwnerManagedAlert from 'src/components/SharedHome/SharedHomeOwnerManagedAlert.svelte';

	export let showSettingsModal = false;
	export let device: Device;

	$: statusStore = getDeviceStatus(device?.id);
	$: settingsStore = getDeviceSettings(device?.id);
	$: status = $statusStore;
	$: settings = $settingsStore as Record<string, unknown> | undefined;
	$: realID = real_id(device?.id);
	$: mode = get_mode(device, status, settings);
	$: settingsReadiness = getDeviceSettingsReadiness({
		deviceId: device?.id,
		settings,
		resource: $deviceRuntimeResource,
	});
	$: settingsReady = settingsReadiness.ready;

	// --- Delete device action ---
	let showDeleteModal = false;
	let deleteDeviceUsage: DeviceUsage = {
		room: [],
		group: [],
		alarm: [],
		scene: [],
		dashboard: [],
		thermostat: [],
	};
	let deleteDeviceIsUsed = false;
	let isGBLE = is_GBLE(device);

	const onDeleteDeviceAction = () => {
		deleteDeviceUsage = {
			room: $rooms[device.room_id] !== undefined ? [$rooms[device.room_id]] : [],
			group: getGroupsWithDevice(device.id),
			alarm: getAlarmsWithDevice(device.id),
			scene: getScenesWithDevice(device.id),
			dashboard: getDashboardsWithDevice(device.id),
			thermostat: getThermostatsWithDevice(device.id),
		} as DeviceUsage;
		deleteDeviceIsUsed = Object.values(deleteDeviceUsage).some((array) => array.length > 0);
		showDeleteModal = true;
	};

	// --- Reboot device action ---
	let showRebootModal = false;

	const rebootDevice = async () => {
		const id = real_id(device.id);
		const gen2 = is_gen_2(device);
		const localIp = get_local_ip(id);
		let res;
		if (gen2) {
			res = await gen2Request(device, 'Shelly.Reboot', {});
		} else {
			if (localIp) {
				res = await localReq(device, `${localIp}/reboot`, 'post', {});
			} else {
				res = await postReq(`/device/reboot`, { id });
			}
		}
		if (res.isok) {
			toast.open({ type: 'success', title: $format('reboot_scc_toast') });
			rebooting_devices.update(id);
		} else {
			toast.open({ type: 'danger', title: $format('reboot_err_toast') });
		}
	};

	$: rebootProps = {
		title: $format('reboot_device_title', { default: 'Reboot Device?' }),
		text: $format('reboot_device_info_text', {
			default:
				'Rebooting the device will restart its operating system, potentially resolving minor issues and improving overall performance. This action will briefly interrupt device functionality.',
		}),
		button_2: $format('button_cancel'),
		button_1: $format('button_reboot_device', { default: 'Reboot device' }),
		callback: rebootDevice,
	};

	// --- Factory reset action ---
	let showFactoryResetModal = false;

	const factoryReset = async () => {
		const id = real_id(device.id);
		const gen2 = is_gen_2(device);
		const localIp = get_local_ip(id);
		let res;
		if (!gen2 && !isGBLE) {
			if (localIp) {
				res = await localReq(device, `${localIp}/settings`, 'post', { reset: 1 });
			} else {
				res = await postReq(`/device/factory_reset?id=${id}`, { id });
			}
		} else {
			res = await gen2Request(device, 'Shelly.FactoryReset', {}, undefined, undefined, 12000);
		}
		if (res.isok) {
			resetDrawerClose();
			toast.open({
				type: 'success',
				title: $format('factory_reset_scc_toast', {
					values: { device_name: device.name },
				}),
				duration: 10000,
			});
			if (isWallDisplay(device.type)) {
				await deleteWallDisplayExtention($devices[real_id(device.id)]);
				return;
			}
			await deleteDeviceHelper(id);
		} else if (res?.errors?.code == 500) {
			await deleteDeviceHelper(id);
		} else {
			toast.open({ type: 'danger', title: $format('factory_reset_err_toast') });
		}
	};

	$: factoryResetProps = {
		title: `${$format('reset_device_to_factory_stngs', {
			default: 'Reset Device to Factory Settings',
		})}?`,
		name: device?.name,
		text: $format('resetting_device_msg', {
			values: { device_name: device.name },
			default: `Resetting the device '{device_name}' to its factory settings will erase all data and settings, including its network configuration. To reconnect, use its Access Point network.`,
		}),
		alert: {
			heading: $format('resseting_device_alert_msg', {
				default:
					'Resetting the device to factory settings will also delete the device from the app.',
			}),
			type: 'danger' as const,
		},
		button_1: $format('button_reset_device', { default: 'Reset device' }),
		button_1_variant: 'danger' as const,
		button_2: $format('button_cancel'),
		callback: factoryReset,
	};

	// --- Transfer device action ---
	let showTransferModal = false;

	const onTransferDeviceAction = () => {
		update.update(() => ({
			value: true,
			subType: '',
			selectAll: false,
			type: 'account_transfer_devices',
			selectedDevices: { [device.id]: device } as any,
		}));
		showTransferModal = true;
	};

	$: if ($sidePanelState.showSettingsModal === false && showSettingsModal === true) {
		showSettingsModal = false;
		setSidePanelState({ showSettingsModal: undefined });
	}

	let initialize = true;
	let isMobile = false;
	let navigationItems: NavigationMenuItemExtendedPropsTypes[] = [];
	let xt1Settings: XT1DeviceSettings[] = [];

	enum IconVariants {
		details = 'fa-circle-info',
		location = 'fa-door-open',
		secondary_controls = 'fa-palette',
		ambient_light = 'fa-lightbulb-on',
		effects = 'fa-stars',
		timer = 'fa-clock',
		schedule = 'fa-calendar',
		internet = 'fa-globe',
		networks = 'fa-globe',
		safety = 'fa-shield',
		actions = 'fa-link',
		webhooks = 'fa-link',
		night_mode = 'fa-moon',
		sensor_actions = 'fak fa-light-sensor-on-link',
		application_settings = 'fa-gears',
		fan_settings = 'fa-fan',
		sensor_settings = 'fak fa-light-sensor-on-gear',
		external_sensors = 'fak fa-light-sensor-on',
		plus_uni_counter = 'fa-gauge-circle-plus',
		virtual_components = 'fa-cube',
		bthome_components = 'fa-cube',
		irrigation_zones_settings = 'fa-droplet',
		irrigation_sequences = 'fa-network-wired',
		peripherals = 'fak fa-light-sensor-on',
		addon_sensors = 'fak fa-light-sensor-on',
		sensor_mute = 'fa-bell-slash',
		temperature_action = 'fa-temperature-arrow-down',
		humidity_action = 'fa-droplet-percent',
		adc_actions = 'fa-exchange',
		temperature_automation = 'fa-temperature-high',
		humidity_automation = 'fa-dewpoint',
		adc_automation = 'fa-tachometer-alt-fast',
		favorites = 'fa-star',
		scripts = 'fa-brackets-curly',
		share = 'fa-share-nodes',
		settings = 'fa-cog',
		charts = 'fa-chart-line',
		notifications = 'fa-bell',
		event_log = 'fa-notebook',
		valve = 'fa-pipe-valve',
		self_test = 'fa-clipboard-list-check',
		find_my_device = 'fa-radar',
		ui_settings = 'fa-sidebar-flip',
		thermostat = 'fa-temperature-list',
		media_player = 'fa-music',
		device_information = 'fa-info',
		transfer_device = 'fa-share-nodes',
		delete_device = 'fa-trash',
		geo_location_and_elevation = 'fa-map-location-dot',
		pill_peripheral_settings = 'fa-gauge-circle-plus',
		bluetooth = 'fa-bluetooth',
		bluetooth_gateway = 'fa-bluetooth',
		motion_settings = 'fa-sensor-on',
		camera_audio = 'fa-volume',
		camera_and_video = 'fa-video',
		tariff_configuration = 'fa-tags',
		camera_zones = 'fa-square-dashed-circle-plus',
		camera_recordings = 'fa-clapperboard-play',
		camera_storage = 'fa-hard-drive',
	}

	const getIconFromTitle = (title: string) => {
		let icon = IconVariants[title];
		if (icon == undefined) {
			return 'fal fa-cog';
		} else if (icon.includes('fak')) {
			return icon;
		} else if (icon.includes('fa-palette')) {
			return 'fa-light ' + icon;
		} else {
			return 'fal ' + icon;
		}
	};

	const ACTION_ITEM_TITLES = new Set([
		'device_reboot',
		'factory_reset',
		'factory_reset_g1',
		'factory_reset_ble',
		'factory_reset_z',
		'transfer_device',
		'delete_device',
	]);

	type ButtonVariant =
		| 'primary'
		| 'secondary'
		| 'danger'
		| 'danger-secondary'
		| 'premium'
		| 'success'
		| 'custom';

	const ACTION_BUTTON_CONFIG: Record<string, { icon: string; variant: ButtonVariant }> = {
		device_reboot: { icon: 'fas fa-arrows-rotate', variant: 'secondary' },
		factory_reset: { icon: 'fas fa-rotate-left', variant: 'secondary' },
		factory_reset_g1: { icon: 'fas fa-rotate-left', variant: 'secondary' },
		factory_reset_ble: { icon: 'fas fa-rotate-left', variant: 'secondary' },
		factory_reset_z: { icon: 'fas fa-rotate-left', variant: 'secondary' },
		transfer_device: { icon: 'fas fa-people-arrows', variant: 'secondary' },
		delete_device: { icon: 'fas fa-trash', variant: 'danger' },
	};

	const ACTION_ITEM_ORDER: Record<string, number> = {
		device_reboot: 0,
		factory_reset: 1,
		factory_reset_g1: 1,
		factory_reset_ble: 1,
		factory_reset_z: 1,
		transfer_device: 2,
		delete_device: 3,
	};

	$: isXT1Device = isXT1Module(device?.type);
	$: settingsMode = isXT1Device && !devices_config[device.type]?.modes?.[mode] ? 'module' : mode;

	const buildDeviceSettings = (
		currentDevice: Device,
		currentSettingsMode: string,
		currentSettings: Record<string, unknown> | undefined,
		currentMode: string,
	) => {
		let configSettings =
			devices_config[currentDevice.type]?.modes[currentSettingsMode]?.settings || [];

		const external_sensors = configSettings?.find(
			(item: { title: string }) => item.title == 'external_sensors',
		);

		if (external_sensors) {
			if (!hasExternalSensors(currentDevice)) {
				configSettings = configSettings.filter(
					(item: { title: string }) =>
						!/external_sensors|temperature_automation|humidity_automation|sensor_actions/.test(
							item.title,
						),
				);
			} else if (!hasExternalSensors(currentDevice, 'humidity')) {
				configSettings = configSettings.filter(
					(item: { title: string }) => !/humidity_automation/.test(item.title),
				);
			}
		}

		const isWallDisplayThermostatEnabled = Object.keys(currentSettings ?? {}).some((key) =>
			key.startsWith('thermostat'),
		);

		// Add sensor settings tab for wall display main device if it doesn't exist
		if (isWallDisplay(currentDevice?.type) && !currentDevice?.id?.includes('_')) {
			const hasSensorSettings = configSettings.some((item) => item.title === 'sensor_settings');
			if (isWallDisplayThermostatEnabled && !hasSensorSettings) {
				configSettings.splice(configSettings.length - 6, 0, {
					title: 'sensor_settings',
					components: [
						{
							title: 'temperature_favorite_positions',
							component: 'temperature_favorite_positions',
						},
					],
				});
			} else if (!isWallDisplayThermostatEnabled && hasSensorSettings) {
				configSettings.splice(
					configSettings.findIndex((item) => item.title === 'sensor_settings'),
					1,
				);
			}
		}

		// Remove settings duplicated as sidepanel tabs
		const BLE_DEVICES_WITH_FIND_ME_TAB = ['SBRC-005B', 'SBBT-002C', 'SBBT-102C'];
		if (currentDevice?.type && BLE_DEVICES_WITH_FIND_ME_TAB.includes(currentDevice.type)) {
			configSettings = configSettings.filter(
				(item: { title: string }) => item.title !== 'find_my_device',
			);
		}
		if (currentDevice?.type === 'S3XT-0S' && currentMode === 'irrigation-controller') {
			configSettings = configSettings.filter(
				(item: { title: string }) => item.title !== 'irrigation_zones_settings',
			);
		}

		return [
			{
				title: 'details',
				components: [{ title: 'details', component: 'device_details' }],
			},
			{
				title: 'location',
				components: [{ title: 'location', component: 'device_location' }],
			},
			...(currentDevice.gen === 'GBLE' && !currentDevice.shared
				? [
						{
							title: 'bluetooth_gateway',
							components: [{ title: 'bluetooth_gateway', component: 'bluetooth_gateway' }],
						},
					]
				: []),
			...configSettings,
		];
	};

	$: deviceSettings = device
		? buildDeviceSettings(device, settingsMode || mode || device.mode, settings, mode)
		: [];
	$: deviceSettingsKey = deviceSettings.map((setting) => setting.title).join('|');
	let cacheActiveTabName = '';
	let lastDeviceSettingsKey = '';

	$: deviceState = getDeviceState(device.id);

	const generateNavigationItems = (tab: string) => {
		if (!device) return [];

		const hasExtractedMediaPlayer = typeof $devices[`${realID}_100`] !== 'undefined';
		const hasExtractedThermostat = typeof $devices[`${realID}_101`] !== 'undefined';

		const isThermostatEnabled = typeof settings?.['thermostat:0'] !== 'undefined';
		const isMediaPlayerEnabled = !!device?.media_player_enabled;
		const hasDeviceInformationSetting = deviceSettings.some((setting) => {
			const settingObj = typeof setting === 'object' ? setting : { title: setting };
			return (
				settingObj.title === 'device_information' ||
				settingObj?.components?.some((comp) => comp?.title === 'device_information')
			);
		});
		const deviceInformationComponent = hasDeviceInformationSetting
			? deviceSettingFormatter(
					device,
					[{ title: 'device_information', component: 'device_information' }],
					settings,
					status,
					tab === 'device_information' ? tab : undefined,
					true,
				).find((component) => component.titleDefault === 'device_information')
			: null;

		const items = deviceSettings
			.map((setting, index) => {
				const settingObj = typeof setting === 'object' ? setting : { title: setting };
				if (settingObj.title === 'device_information') return null;
				// FE-2007: keep the change_view entry on shared homes; its
				// ViewSwitching content renders disabled with an explanation.
				const filteredComponents =
					settingObj?.components?.filter((comp) => comp?.title !== 'device_information') ?? [];
				if (settingObj?.components?.length > 0 && filteredComponents.length === 0) return null;

				const components = deviceSettingFormatter(
					device,
					filteredComponents,
					settings,
					status,
					settingObj.title,
					true,
				);

				const formattedTitles = new Set(components.map((c) => c.titleDefault));
				const navComponentsSource = filteredComponents.filter((comp) =>
					formattedTitles.has(comp.title),
				);
				if (settingObj?.components?.length > 0 && navComponentsSource.length === 0) {
					return null;
				}

				let innerNav =
					(settingObj.title === 'application_settings' && navComponentsSource?.length >= 1) ||
					navComponentsSource?.length > 1
						? navComponentsSource.map((comp) => ({
								id: comp.title,
								parentID: settingObj.title,
								title: comp.title,
								hideAvatar: true,
								active: false,
								disabled: isTabDisabled(device, $deviceState, comp.title),
								component: components.find(
									(component) => comp.title === component.titleDefault,
								),
							}))
						: undefined;

				if (settingObj.title === 'settings' && isXT1Device && xt1Settings.length > 0) {
					innerNav = [
						...getXT1SettingsNavigationItems(device, xt1Settings),
						...(innerNav ?? []),
					];
				}

				let disabled = isTabDisabled(device, $deviceState, settingObj.title);

				// temporary disable the fav. positions tab for gen 2 cover devices
				if (is_gen_2(device) && /favorites/.test(settingObj.title)) {
					disabled = true;
				}

				if (settingObj.title === 'share' && isActiveHomeShared($homes.list)) {
					disabled = true;
				}

				return {
					id: settingObj.title,
					title: settingObj.title,
					avatarProps: { icon: getIconFromTitle(settingObj.title) },
					active: settingObj.title === tab,
					disabled,
					...(innerNav && innerNav.length > 0
						? { innerNav }
						: {
								component:
									navComponentsSource?.length === 1
										? components.find(
												(component) =>
													navComponentsSource[0].title ===
													component.titleDefault,
											)
										: components.find(
												(component) =>
													settingObj.title === component.titleDefault,
											),
							}),
				};
			})
			.filter(Boolean);

		// Inject transfer_device and delete_device action items into
		// the Device Settings innerNav for the sidepanel layout
		const settingsItem = items.find((item) => item.title === 'settings');
		if (settingsItem?.innerNav && hasDeviceInformationSetting) {
			const canTransfer =
				!device.shared &&
				!device.shared_by_me &&
				!device.id.includes('_') &&
				!isWallDisplay(device.type) &&
				!$user_data?.installer;

			const actionSettings = [
				...(canTransfer ? [{ title: 'transfer_device', component: 'transfer_device' }] : []),
				{ title: 'delete_device', component: 'delete_device' },
			];

			const actionComponents = deviceSettingFormatter(
				device,
				actionSettings,
				settings,
				status,
				undefined,
				true,
			);

			actionSettings.forEach((setting) => {
				const component = actionComponents.find((c) => c.titleDefault === setting.title);
				if (component) {
					settingsItem.innerNav.push({
						id: setting.title,
						parentID: 'settings',
						title: setting.title,
						hideAvatar: true,
						active: false,
						// FE-2007: shared homes keep the action visible but disabled;
						// an inline explanation is shown above the action buttons.
						disabled:
							isTabDisabled(device, $deviceState, setting.title) ||
							isActiveHomeShared($homes.list),
						component,
					});
				}
			});
		}

		// Add extracted peripheral device tabs after Actions tab for wall display devices
		if (isWallDisplay(device.type) && device.category === 'relay') {
			const actionsIndex = items.findIndex((item) => item.title === 'actions');
			if (actionsIndex !== -1) {
				const extractedTabs = [];

				// Add thermostat tab if enabled
				if (isThermostatEnabled) {
					const thermostatDevice = hasExtractedThermostat ? $devices[`${realID}_101`] : device;
					if (thermostatDevice) {
						extractedTabs.push({
							id: 'thermostat',
							title: 'thermostat',
							avatarProps: { icon: getIconFromTitle('thermostat') },
							active: tab === 'thermostat',
							disabled: false,
							component: {
								component: ThermostatsG2,
								titleDefault: 'thermostat',
								props: { device: thermostatDevice },
							},
						});
					}
				}

				// Add media player tab if enabled
				if (isMediaPlayerEnabled) {
					const mediaPlayerDevice = hasExtractedMediaPlayer
						? $devices[`${realID}_100`]
						: device;
					if (mediaPlayerDevice) {
						extractedTabs.push({
							id: 'media_player',
							title: 'media_player',
							avatarProps: { icon: getIconFromTitle('media_player') },
							active: tab === 'media_player',
							disabled: false,
							component: {
								component: MediaPlayerComponent,
								titleDefault: 'media_player',
								props: { device: mediaPlayerDevice },
							},
						});
					}
				}

				// Insert extracted tabs after Actions
				if (extractedTabs.length > 0) {
					items.splice(actionsIndex + 1, 0, ...extractedTabs);
				}
			}
		}

		if (deviceInformationComponent) {
			items.push({
				id: 'device_information',
				title: 'device_information',
				avatarProps: { icon: getIconFromTitle('device_information') },
				active: tab === 'device_information',
				disabled: isTabDisabled(device, $deviceState, 'device_information'),
				component: deviceInformationComponent,
			});
		}

		return items;
	};

	let activeNavItem;
	let lastSharedHomeActive = isActiveHomeShared($homes.list);
	$: {
		const sharedHomeActive = isActiveHomeShared($homes.list);
		if (showSettingsModal && device && !initialize && sharedHomeActive !== lastSharedHomeActive) {
			lastSharedHomeActive = sharedHomeActive;
			const tab = cacheActiveTabName || deviceSettings[0]?.title;
			navigationItems = generateNavigationItems(tab);
		}
	}

	$: {
		if (
			showSettingsModal &&
			device &&
			deviceSettings.length > 0 &&
			deviceSettingsKey !== lastDeviceSettingsKey
		) {
			const tab = initialize
				? deviceSettings[0]?.title
				: cacheActiveTabName || activeNavItem?.title || deviceSettings[0]?.title;
			navigationItems = generateNavigationItems(tab);
			lastDeviceSettingsKey = deviceSettingsKey;
			if (initialize) {
				initialize = false;
			}
		} else if (activeNavItem && !activeNavItem.parentID) {
			if (cacheActiveTabName !== activeNavItem.title) {
				navigationItems = generateNavigationItems(activeNavItem.title);
				cacheActiveTabName = activeNavItem.title;
			}
		}

		activeNavItem = findActiveItem(navigationItems);
		if (typeof activeNavItem === 'undefined') {
			onClickNavigationItem(navigationItems.find((item) => !item.disabled));
		}
	}

	const findActiveItem = (
		items: NavigationMenuItemExtendedPropsTypes[],
	): NavigationMenuItemExtendedPropsTypes | undefined => {
		for (const item of items) {
			// If this item has innerNav, check inside first
			if (item.innerNav && item.innerNav.length > 0) {
				const activeInner = findActiveItem(item.innerNav);
				if (activeInner) {
					return activeInner; // Return active inner item
				}
			}

			// If no active innerNav found, but this item itself is active
			if (item.active && !item.disabled) {
				return item; // Return this item as active
			}
		}

		return undefined;
	};

	let hideLeftContent = false;

	$: hideRightContent = isMobile ? true : false;

	$: if (!isMobile) {
		//show left content when resize window and in inner page
		hideLeftContent = false;
	}

	$: modalTitle = !hideLeftContent
		? $format('settings', { default: 'Settings' })
		: activeNavItem?.parentID
			? capFirstChar(
					$format(`sub_settings_title_${activeNavItem.title}`, {
						default: activeNavItem.title.replace(/_/g, ' '),
					}),
				)
			: capFirstChar(
					$format(`settings_title_${activeNavItem.title}`, {
						default: activeNavItem.title.replace(/_/g, ' '),
					}),
				);

	$: breadCrumbs = genBreadcrumbs(navigationItems, activeNavItem?.id);

	$: deviceID = $sidePanelState.id;

	$: notificationsToShow = getNotificationsForDevice(deviceID);

	$: criticalNotifications = getCriticalNotifications(notificationsToShow);

	//Handle routeToDeviceSettings navigation (must run before notifications redirect so CTA targets are honoured)
	let pendingRoute: { setting: string; subSetting?: string } | null = null;
	let routeHandled = false;
	let skipNotificationsRedirectThisCycle = false;

	// Watch for routes and store them for processing
	$: {
		if (
			Object.keys($routeToDeviceSettings).length > 0 &&
			$routeToDeviceSettings[device.id] &&
			$routeToDeviceSettings &&
			!routeHandled
		) {
			const route = $routeToDeviceSettings[device.id];
			if (route.setting) {
				pendingRoute = { setting: route.setting, subSetting: route.subSetting };
				// Open modal asynchronously to avoid cyclical dependency
				if (!showSettingsModal) {
					tick().then(() => {
						showSettingsModal = true;
					});
				}
			}
		} else if (
			Object.keys($routeToDeviceSettings).length === 0 ||
			!$routeToDeviceSettings[device.id] ||
			!$routeToDeviceSettings
		) {
			pendingRoute = null;
			routeHandled = false;
		}
	}

	// Process pending route when modal is open and navigation items are ready
	$: {
		if (pendingRoute && showSettingsModal && navigationItems.length > 0 && !routeHandled) {
			routeHandled = true;
			skipNotificationsRedirectThisCycle = true;
			const { setting, subSetting } = pendingRoute;
			// Determine target nav item ID: subSetting takes precedence, otherwise use setting
			const targetId = subSetting || setting;

			// Check if the target item exists in navigation items
			const targetItem = findItemByID(navigationItems, targetId);
			if (targetItem) {
				if (Array.isArray(activeNavItem?.innerNav)) {
					activeNavItem.innerNav = activeNavItem.innerNav.map((item) => ({
						...item,
						active: item.id === targetId,
					}));
				}
				// Navigate to the target item
				redirectToNavItemID(targetId);
			}
			// Reset route after handling (even if item not found)
			routeToDeviceSettings.resetTarget();
			pendingRoute = null;
			// Reset flag after a brief delay to allow for future routes
			setTimeout(() => {
				routeHandled = false;
			}, 100);
		}
	}

	// Manage redirects (run after route handling so routeToDeviceSettings target is applied first)
	$: $sidePanelState.open ? handleRedirectToItem() : null;
	$: showSettingsModal ? handleRedirectToItem() : null;

	const resetActiveItems = (
		items: NavigationMenuItemExtendedPropsTypes[],
	): NavigationMenuItemExtendedPropsTypes[] => {
		return items.map((item, i) => ({
			...item,
			active: false,
			...(item.innerNav && { innerNav: resetActiveItems(item.innerNav) }),
		}));
	};

	const handleDetailsSaved = (payload: {
		roomChanged: boolean;
		roomId?: number;
		shared?: boolean;
	}) => {
		onCloseSettings();
		if (payload.roomChanged && payload.roomId !== undefined) {
			setSidePanelState({ open: false });
			if (payload.shared && payload.roomId === -20) {
				replace('/home/devices/-20');
			} else {
				replace(`/home/room/${payload.roomId}/devices`);
			}
		}
	};

	setContext('onDetailsSaved', handleDetailsSaved);

	const stickyBottomAction = writable<{
		onClick: () => void;
		loading: boolean;
		disabled: boolean;
		label?: string;
		secondaryButton?: {
			onClick: () => void;
			label: string;
			variant?: string;
		};
		gap?: string;
	} | null>(null);
	setContext('stickyBottomAction', stickyBottomAction);

	const onCloseSettings = () => {
		showSettingsModal = false;
		// Clear the store value so it doesn't prevent reopening
		setSidePanelState({ showSettingsModal: undefined });
		hideLeftContent = false;

		if (isMobile) {
			hideRightContent = true;

			if ($app) {
				if ($controlBleDevice.connectionState === 2) {
					$os === 'IOS'
						? control_bluetooth_device_ios(device.id, 'disconnect')
						: control_bluetooth_device_android(device.id, 'disconnect');
				} else {
					$os === 'IOS'
						? control_bluetooth_device_ios(device.id, 'abortConnecting')
						: control_bluetooth_device_android(device.id, 'disconnect');
				}
				controlBleDevice.reset();
			}
		}

		// Clear active items and set first as active
		navigationItems = resetActiveItems(navigationItems);
		navigationItems[0].active = true;
		activeNavItem = navigationItems[0];
		initialize = true;
		cacheActiveTabName = '';
		lastDeviceSettingsKey = '';
		skipNotificationsRedirectThisCycle = false;
	};

	const setActiveNavItem = (
		items: NavigationMenuItemExtendedPropsTypes[],
		activeItem: NavigationMenuItemExtendedPropsTypes,
	) => {
		return items.map((item) => {
			const updatedInnerNav: NavigationMenuItemExtendedPropsTypes[] = item.innerNav
				? setActiveNavItem(item.innerNav, activeItem)
				: [];

			return {
				...item,
				active: item.id === activeItem?.id || updatedInnerNav.some((item) => item.active),
				...(updatedInnerNav?.length && { innerNav: updatedInnerNav }),
			};
		});
	};

	const handleMobileContentRender = () => {
		if (isMobile) {
			hideRightContent = false;
			hideLeftContent = true;
		}
	};

	const onClickNavigationItem = (selectedItem: NavigationMenuItemExtendedPropsTypes) => {
		navigationItems = setActiveNavItem(navigationItems, selectedItem);
		activeNavItem = selectedItem;

		handleMobileContentRender();
	};

	const genBreadcrumbs = (
		items: NavigationMenuItemExtendedPropsTypes[],
		activeId?: string,
	): NavigationMenuItemExtendedPropsTypes[] => {
		if (!activeId) return [];

		for (const item of items) {
			if (item.id === activeId) {
				return [item]; // found the active item
			}

			if (item.innerNav) {
				const childPath = genBreadcrumbs(item.innerNav, activeId);
				if (childPath.length > 0) {
					return [item, ...childPath]; // prepend parent to the path
				}
			}
		}

		return []; // not found
	};

	const findItemByID = (
		items: NavigationMenuItemExtendedPropsTypes[],
		activeId: string,
	): NavigationMenuItemExtendedPropsTypes => {
		for (const item of items) {
			// If this item has innerNav, check inside first
			if (item?.innerNav) {
				const activeInner = findItemByID(item.innerNav, activeId);
				if (activeInner) {
					return activeInner; // Return active inner item
				}
			}

			// If no active innerNav found
			if (item.id === activeId) {
				return item; // Return this item as active
			}
		}

		return undefined;
	};

	const onBack = () => {
		if (activeNavItem.parentID) {
			activeNavItem = findItemByID(navigationItems, activeNavItem.parentID);
			navigationItems = setActiveNavItem(navigationItems, activeNavItem);
		} else {
			hideRightContent = true;
			hideLeftContent = false;
		}
	};

	export const redirectToNavItemID = (navItemID: string) => {
		if (!showSettingsModal) showSettingsModal = true;
		activeNavItem = findItemByID(navigationItems, navItemID);
		navigationItems = setActiveNavItem(navigationItems, activeNavItem);
		handleMobileContentRender();
	};

	const redirectToNotifications = () => {
		if (!criticalNotifications?.length) return;
		if (!showSettingsModal) return; // Only redirect when user has already opened settings; do not auto-open the modal
		// Skip when we just processed a routeToDeviceSettings target (e.g. CTA to temperature_favorite_positions).
		if (skipNotificationsRedirectThisCycle) return;
		if (routeHandled) return; // Explicit route was just applied; don't override for 100ms.
		// When the user explicitly navigated to a setting via routeToDeviceSettings, honour that.
		if (
			Object.keys($routeToDeviceSettings).length > 0 &&
			$routeToDeviceSettings[device?.id]?.setting
		)
			return;

		const notificationsID = 'notifications';
		redirectToNavItemID(notificationsID);
	};

	const handleRedirectToItem = () => redirectToNotifications();

	onMount(() => {
		if (mobile.isMobile) {
			mobile.addEventListener('back_button', onBack);
		}

		if (isXT1Device) {
			fetchXT1DeviceSettings(device).then((settings) => {
				xt1Settings = settings;
				const activeTab =
					activeNavItem?.parentID || activeNavItem?.title || deviceSettings[0]?.title;
				navigationItems = generateNavigationItems(activeTab);
				cacheActiveTabName = activeTab;
			});
		}

		return () => {
			if (mobile.isMobile) {
				mobile.removeEventListener('back_button', onBack);
			}
		};
	});
</script>

<Modal
	on:close={onCloseSettings}
	showModal={showSettingsModal}
	title={modalTitle}
	customContent
	hideSlotHeaderActionsLeft={!hideLeftContent}
	preserveMaxHeight={!isMobile}
>
	<svelte:fragment slot="headerActionsLeft">
		<IconButton
			aria-label="Back Button"
			variant="secondary"
			icon="fas fa-arrow-left"
			on:click={onBack}
		/>
	</svelte:fragment>

	<ModalContentLayout
		slot="content"
		{hideRightContent}
		{hideLeftContent}
		hideStaticTop={isMobile}
		bind:isMobile
		hideStickyBottom={!$stickyBottomAction}
	>
		<Stack slot="leftContent" gap={isMobile ? 0 : 4}>
			{#each navigationItems as item, i}
				<NavigationItem
					drillDownNoBorder={i === navigationItems.length - 1}
					title={capFirstChar(
						$format(`settings_title_${item.title}`, {
							default: item.title.replace(/_/g, ' '),
						}),
					)}
					active={item.active}
					avatarProps={item.avatarProps}
					disabled={item.disabled}
					type={isMobile ? 'drilldown' : 'default'}
					on:click={() => {
						onClickNavigationItem(item);
					}}
				>
					<LabelV2
						size="s"
						slot="trailing"
						{...handleCollapseTitleLabel({ titleDefault: item.title })}
					/>
				</NavigationItem>
			{/each}
		</Stack>

		<Breadcrumbs
			aria-label="Breadcrumbs"
			class="mb-m pt-m"
			slot="staticTop"
			type="buttons"
			links={breadCrumbs.map((item, i) => ({
				label: capFirstChar(
					$format(`${i === 1 ? 'sub_' : ''}settings_title_${item.title}`, {
						default: item.title.replace(/_/g, ' '),
					}),
				),
				href: undefined,
				onClick: i === breadCrumbs.length - 1 ? undefined : () => onClickNavigationItem(item),
			}))}
		/>

		<svelte:fragment slot="content">
			{#if !settingsReady}
				{#if settingsReadiness.phase === 'error'}
					<Alert
						type="danger"
						heading={$format('error_global')}
						message={$format('error_global')}
						isHeadingBold
					/>
				{:else}
					<div class="d-flex justify-content-center align-items-center height-full">
						<Loader size="m" />
					</div>
				{/if}
			{:else if activeNavItem}
				{#if activeNavItem?.innerNav}
					{#if activeNavItem.title === 'settings' && device.gen === 'GBLE' && !device.shared}
						{#if !$app}
							<Alert
								type="warning"
								isHeadingBold
								heading={$format('alert_heading_ble_devices_restricted', {
									default: 'Settings Restricted',
								})}
								message={$format('alert_message_ble_devices_restricted', {
									default:
										'Bluetooth devices can be configured only via the Shelly Smart Control application.',
								})}
								class="mb-m"
							/>
						{/if}

						{#if $app && !isSupportedVersion($os === 'IOS' ? '1.0.5' : '1.14.0')}
							<Alert
								type="warning"
								heading={$format('alert_heading_native_update_required', {
									default: 'App update required',
								})}
								message={$format('alert_message_native_update_required', {
									default:
										'Please update the application to the latest version for full support of these features.',
								})}
								class="mb-m"
							/>
						{/if}

						<BluetoothSettings {device} usedInSidepanel />
					{/if}
					{@const settingItems = activeNavItem.innerNav.filter(
						(item) => !ACTION_ITEM_TITLES.has(item.title),
					)}
					{@const actionItems = activeNavItem.innerNav
						.filter((item) => ACTION_ITEM_TITLES.has(item.title))
						.sort(
							(a, b) =>
								(ACTION_ITEM_ORDER[a.title] ?? 99) - (ACTION_ITEM_ORDER[b.title] ?? 99),
						)}
					<Container
						padding="0"
						type={isMobile ? undefined : 'borderSolid'}
						style="overflow: hidden;"
						backgroundColor="color-base"
					>
						<Stack gap={0}>
							{#each settingItems as item, i}
								<NavigationItem
									class={isMobile ? '' : 'pr-m pl-m'}
									drillDownNoBorder={i === settingItems.length - 1}
									title={capFirstChar(
										$format(`sub_settings_title_${item.title}`, {
											default: item.title.replace(/_/g, ' '),
										}),
									)}
									active={item.active}
									avatarProps={item.avatarProps}
									disabled={item.disabled}
									type="drilldown"
									hideAvatar
									on:click={() => {
										onClickNavigationItem(item);
									}}
								>
									<LabelV2
										size="s"
										slot="trailing"
										{...handleCollapseTitleLabel(item.component)}
									/>
								</NavigationItem>
							{/each}
						</Stack>
					</Container>
					{#if actionItems.length > 0}
						{#if isActiveHomeShared($homes.list)}
							<SharedHomeOwnerManagedAlert class="mt-m" context="generic" />
						{/if}
						<Stack gap={8} class="mt-m">
							{#each actionItems as item}
								{@const config = ACTION_BUTTON_CONFIG[item.title] ?? {
									icon: 'fas fa-cog',
									variant: /** @type {ButtonVariant} */ ('secondary'),
								}}
								<Button
									size="m"
									width="full"
									variant={config.variant}
									icon={config.icon}
									disabled={item.disabled}
									on:click={() => {
										if (item.title === 'delete_device') {
											onDeleteDeviceAction();
										} else if (item.title === 'device_reboot') {
											showRebootModal = true;
										} else if (item.title === 'factory_reset') {
											if (isGBLE) {
												onClickNavigationItem(item);
											} else {
												showFactoryResetModal = true;
											}
										} else if (item.title === 'transfer_device') {
											onTransferDeviceAction();
										} else {
											onClickNavigationItem(item);
										}
									}}
								>
									{item.title === 'delete_device' && device.shared
										? capFirstChar(
												$format('button_remove_device', {
													default: 'Remove device',
												}),
											)
										: capFirstChar(
												$format(`sub_settings_title_${item.title}`, {
													default: item.title.replace(/_/g, ' '),
												}),
											)}
								</Button>
							{/each}
						</Stack>
					{/if}
				{:else if device && activeNavItem?.component}
					<div class={`height-full`}>
						<DeviceSettingsItem {device} setting={activeNavItem.component} />
					</div>
				{/if}
			{:else}
				<div class="d-flex justify-content-center align-items-center">
					<Loader size="m" />
				</div>
			{/if}
		</svelte:fragment>
		<SidePanelSettingsModalStickyBottom slot="stickyBottom" />
	</ModalContentLayout>
</Modal>

{#if showDeleteModal}
	<DeleteDeviceModal
		bind:showModalDelete={showDeleteModal}
		{device}
		deviceIsUsed={deleteDeviceIsUsed}
		deviceUsage={deleteDeviceUsage}
		on:close={() => {
			showDeleteModal = false;
		}}
	/>
{/if}

{#if showRebootModal}
	<ModalPrompt bind:showModal={showRebootModal} props={rebootProps} />
{/if}

{#if showFactoryResetModal}
	<FactoryResetModal bind:showModal={showFactoryResetModal} props={factoryResetProps} />
{/if}

{#if showTransferModal}
	<BatchTransferModal bind:showModal={showTransferModal} areFiltersDisabled={false} />
{/if}
